import React, { useMemo } from 'react';
import { Alert, Button, Card, Col, Row, Space, Statistic, Tag, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store';
import { useGetGovernanceOverviewQuery } from '../../store/api/managementApi';
const { Text } = Typography;

type Props = {
  compact?: boolean;
  title?: string;
};

function riskColorByLevel(level: string | undefined): string {
  const normalized = String(level || '').toUpperCase();
  if (normalized === 'CRITICAL') return 'red';
  if (normalized === 'HIGH') return 'volcano';
  if (normalized === 'ELEVATED' || normalized === 'WARNING') return 'orange';
  return 'green';
}

function pressureLabel(level: string | undefined): string {
  const normalized = String(level || '').toUpperCase();
  if (normalized === 'CRITICAL') return 'Критическая нагрузка';
  if (normalized === 'HIGH') return 'Высокая нагрузка';
  if (normalized === 'ELEVATED') return 'Повышенная нагрузка';
  return 'Нагрузка стабильна';
}

function escalationLabel(level: string | undefined, hasAttention: boolean): string {
  const normalized = String(level || '').toUpperCase();
  if (normalized === 'HIGH') return 'Требует срочного контроля';
  if (normalized === 'ELEVATED') return 'Требует внимания';
  if (hasAttention) return 'Требует внимания';
  return 'Критичных проблем нет';
}

function backlogLabel(level: string | undefined): string {
  const normalized = String(level || '').toUpperCase();
  if (normalized === 'CRITICAL') return 'Очередь критическая';
  if (normalized === 'WARNING') return 'Очередь растет';
  return 'Очередь задач стабильна';
}

function riskText(riskCode: string): string {
  switch (riskCode) {
    case 'CRITICAL_SLA_BREACH':
      return 'Критические просрочки SLA';
    case 'FINANCE_RECONCILIATION_PRESSURE':
      return 'Финансовые расхождения требуют проверки';
    case 'ACCOUNTANT_BACKLOG_PRESSURE':
      return 'Бухгалтерия перегружена';
    case 'SCHEDULER_JOB_FAILURES':
      return 'Сбои фоновых задач';
    case 'STALE_WORKFLOWS':
      return 'Зависшие процессы';
    default:
      return riskCode;
  }
}

function severityLabel(severity: string | undefined): string {
  const value = String(severity || '').toUpperCase();
  if (value === 'CRITICAL') return 'Критично';
  if (value === 'WARNING') return 'Требует внимания';
  return 'Стабильно';
}

function priorityColor(priority: string | undefined): string {
  const value = String(priority || '').toUpperCase();
  if (value === 'CRITICAL') return 'red';
  if (value === 'HIGH') return 'volcano';
  if (value === 'ATTENTION') return 'orange';
  return 'default';
}

export default function GovernanceRiskHeader({ compact = false, title = 'Governance Risk Header' }: Props) {
  const navigate = useNavigate();
  const { data, isLoading } = useGetGovernanceOverviewQuery();
  const role = String(useSelector((state: RootState) => state.auth.user?.role?.name || '')).toLowerCase();

  const roleActions = useMemo(() => {
    if (role === 'logist') {
      return [
        { key: 'routes_risk', label: 'Маршруты с рисками', to: '/routes' },
        { key: 'workspace_overdue', label: 'Просроченные задачи', to: '/workspace' },
      ];
    }
    if (role === 'accountant') {
      return [
        { key: 'finance_workspace', label: 'Финансовые очереди', to: '/finance/workspace' },
        { key: 'finance_payments', label: 'Реестр платежей', to: '/finance/payments' },
      ];
    }
    if (role === 'admin' || role === 'manager' || role === 'director') {
      return [
        { key: 'governance_overview', label: 'Центр управления', to: '/dashboard' },
        { key: 'workspace', label: 'Операционное пространство', to: '/workspace' },
        { key: 'finance_workspace', label: 'Финансовое пространство', to: '/finance/workspace' },
      ];
    }
    return [
      { key: 'workspace', label: 'Рабочее пространство', to: '/workspace' },
      { key: 'routes', label: 'Маршруты', to: '/routes' },
    ];
  }, [role]);

  const topRisks = (data?.escalationSummary?.topOperationalRisks || []).slice(0, compact ? 3 : 5);
  const blockerReasons = (data?.blockerReasons || []).slice(0, compact ? 2 : 4);
  const rankedBlockers = (data?.prioritization?.rankedBlockers || []).slice(0, compact ? 2 : 5);
  const criticalRanked = rankedBlockers.filter((item) => item.priorityLevel === 'CRITICAL');
  const attentionRanked = rankedBlockers.filter((item) => item.priorityLevel === 'ATTENTION' || item.priorityLevel === 'HIGH');
  const hasOperationalAttention =
    (data?.workflowMetrics?.overdueTasks ?? 0) > 0 ||
    (data?.workflowStall?.staleWorkflowCount ?? 0) > 0 ||
    (data?.queueAging?.tasks || []).some((item) => (item.bucket === 'OVERDUE' || item.bucket === 'CRITICAL_DELAY') && item.count > 0) ||
    rankedBlockers.length > 0;
  const agingSummary = (data?.queueAging?.tasks || []).filter((item) => item.count > 0);
  const coordinationOrdering = (data?.coordination?.actionOrdering || []).slice(0, compact ? 2 : 3);
  const coordinationQueues = (data?.coordination?.queueReduction || []).slice(0, compact ? 2 : 3);
  const coordinationCrossRole = (data?.coordination?.crossRoleVisibility || []).slice(0, compact ? 2 : 3);
  const bottleneckText = useMemo(() => {
    if (!data) return [];
    return [
      `${data.operationalLoad?.accountantBacklog ?? 0} задач ожидают бухгалтерию`,
      `${data.workflowMetrics?.criticalOverdueTasks ?? 0} критических просрочек`,
      `${data.financeGovernance?.reconciliationAnomalies ?? 0} финансовых проблем требуют проверки`,
      `${data.workflowStall?.stalledByDocuments ?? 0} процессов ждут документы`,
      `${data.workflowStall?.stalledFinanceApprovals ?? 0} процессов ждут финансовое подтверждение`,
    ];
  }, [data]);

  return (
    <Card title={title} size={compact ? 'small' : 'default'}>
      {data?.pressure?.level && data.pressure.level !== 'NORMAL' && (
        <Alert
          type={data.pressure.level === 'CRITICAL' ? 'error' : 'warning'}
          showIcon
          style={{ marginBottom: 12 }}
          message={pressureLabel(data.pressure.level)}
          description={`Требует контроля: ${data?.escalationSummary?.escalationPressure === 'HIGH' ? 'да' : 'умеренно'}.`}
        />
      )}
      {Boolean(data?.workflowStall?.isQueueShrinkingRisk) && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Очередь уменьшается медленно"
          description="Процесс не двигается равномерно: есть признаки зависания в операционных очередях."
        />
      )}
      <Row gutter={[12, 12]}>
        <Col xs={24} sm={12} md={8} lg={compact ? 12 : 6}>
          <div style={{ cursor: 'pointer' }} onClick={() => navigate('/workspace')}>
            <Statistic
              title="Критические просрочки"
              loading={isLoading}
              value={data?.workflowMetrics?.criticalOverdueTasks ?? 0}
              valueStyle={{ color: '#cf1322' }}
            />
          </div>
        </Col>
        <Col xs={24} sm={12} md={8} lg={compact ? 12 : 6}>
          <div style={{ cursor: 'pointer' }} onClick={() => navigate('/workspace')}>
            <Statistic
              title="Критические нарушения SLA"
              loading={isLoading}
              value={data?.slaSummary?.criticalBreach ?? 0}
              valueStyle={{ color: '#cf1322' }}
            />
          </div>
        </Col>
        <Col xs={24} sm={12} md={8} lg={compact ? 12 : 6}>
          <div style={{ cursor: 'pointer' }} onClick={() => navigate('/finance/workspace')}>
            <Statistic
              title="Финансовые проблемы"
              loading={isLoading}
              value={data?.financeGovernance?.reconciliationAnomalies ?? 0}
              valueStyle={{ color: '#d46b08' }}
            />
          </div>
        </Col>
        <Col xs={24} sm={12} md={8} lg={compact ? 12 : 6}>
          <div style={{ cursor: 'pointer' }} onClick={() => navigate('/dashboard')}>
            <Statistic
              title="Сбои фоновых задач"
              loading={isLoading}
              value={data?.operationalHealth?.failedJobs ?? 0}
              valueStyle={{ color: '#722ed1' }}
            />
          </div>
        </Col>
      </Row>

      <Space wrap style={{ marginTop: 10 }}>
        <Tag color={riskColorByLevel(data?.pressure?.level)}>{pressureLabel(data?.pressure?.level)}</Tag>
        <Tag color={riskColorByLevel(data?.escalationSummary?.escalationPressure)}>
          {escalationLabel(data?.escalationSummary?.escalationPressure, hasOperationalAttention)}
        </Tag>
        <Tag color={riskColorByLevel(data?.operationalLoad?.backlogSeverity)}>
          {backlogLabel(data?.operationalLoad?.backlogSeverity)}
        </Tag>
      </Space>

      {Boolean(data?.prioritization?.summary) && (
        <Space wrap style={{ marginTop: 10 }}>
          <Tag color="red">🚨 Критично: {data?.prioritization?.summary?.criticalNow ?? 0}</Tag>
          <Tag color="orange">⚠ Требует внимания: {(data?.prioritization?.summary?.highPriority ?? 0) + (data?.prioritization?.summary?.attentionNeeded ?? 0)}</Tag>
        </Space>
      )}

      {agingSummary.length > 0 && (
        <Space wrap style={{ marginTop: 10 }}>
          {agingSummary.map((item) => (
            <Tag key={item.bucket} color={item.bucket === 'CRITICAL_DELAY' ? 'red' : item.bucket === 'OVERDUE' ? 'volcano' : 'blue'}>
              {item.label}: {item.count}
            </Tag>
          ))}
        </Space>
      )}
      {Boolean(data?.completionVisibility) && (
        <Space wrap style={{ marginTop: 10 }}>
          <Tag color="blue">Физически завершено: {data?.completionVisibility?.physicallyCompleted ?? 0}</Tag>
          <Tag color="green">Полностью закрыто: {data?.completionVisibility?.fullyClosed ?? 0}</Tag>
          {(data?.completionVisibility?.physicallyCompletedButNotClosed ?? 0) > 0 && (
            <Tag color="volcano">
              Требует закрытия после рейса: {data?.completionVisibility?.physicallyCompletedButNotClosed ?? 0}
            </Tag>
          )}
        </Space>
      )}

      {!compact && (
        <>
          <div style={{ marginTop: 10 }}>
            {bottleneckText.map((line) => (
              <div key={line}>
                <Text type="secondary">- {line}</Text>
              </div>
            ))}
          </div>
          {topRisks.length > 0 && (
            <Space wrap style={{ marginTop: 10 }}>
              {topRisks.map((risk) => (
                <Tag key={risk} color="magenta">
                  {riskText(risk)}
                </Tag>
              ))}
            </Space>
          )}
          {blockerReasons.length > 0 && (
            <div style={{ marginTop: 12 }}>
              {blockerReasons.map((item) => (
                <Card key={item.reasonCode} size="small" style={{ marginBottom: 8 }}>
                  <Space direction="vertical" size={2}>
                    <Text strong>{item.reason}</Text>
                    <Text type="secondary">
                      {severityLabel(item.severity)} | {item.count} | {item.aging.label}
                    </Text>
                    <Text type="secondary">{item.priorityLabel} | Срочность: {item.urgencyScore}</Text>
                    <Text>Ответственный: {item.owner}</Text>
                    <Text>Следующее действие: {item.nextAction}</Text>
                  </Space>
                </Card>
              ))}
            </div>
          )}
          {criticalRanked.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <Text strong>🚨 Критично</Text>
              {criticalRanked.map((item) => (
                <Card key={`critical_${item.reasonCode}`} size="small" style={{ marginTop: 6 }}>
                  <Space direction="vertical" size={2}>
                    <Space wrap>
                      <Tag color={priorityColor(item.priorityLevel)}>{item.priorityLabel}</Tag>
                      <Tag>Срочность: {item.urgencyScore}</Tag>
                    </Space>
                    <Text>{item.criticalityExplanation}</Text>
                    <Text type="secondary">{item.criticalPath.explanation}</Text>
                    <Text>Ответственный: {item.owner}</Text>
                    <Text>Действие: {item.nextAction}</Text>
                  </Space>
                </Card>
              ))}
            </div>
          )}
          {attentionRanked.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <Text strong>⚠ Требует внимания</Text>
              {attentionRanked.map((item) => (
                <Card key={`attention_${item.reasonCode}`} size="small" style={{ marginTop: 6 }}>
                  <Space direction="vertical" size={2}>
                    <Space wrap>
                      <Tag color={priorityColor(item.priorityLevel)}>{item.priorityLabel}</Tag>
                      <Tag>Срочность: {item.urgencyScore}</Tag>
                    </Space>
                    <Text>{item.reason}</Text>
                    <Text type="secondary">
                      Влияние: {item.operationalImpact.map((impact) => impact.label).join(', ')}
                    </Text>
                  </Space>
                </Card>
              ))}
            </div>
          )}
          {coordinationOrdering.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <Text strong>Что сделать сначала</Text>
              {coordinationOrdering.map((item) => (
                <Card key={`coord_order_${item.step}_${item.title}`} size="small" style={{ marginTop: 6 }}>
                  <Space direction="vertical" size={2}>
                    <Text strong>
                      {item.step}. {item.title}
                    </Text>
                    <Text>Ответственный: {item.owner}</Text>
                    <Text>Действие: {item.nextAction}</Text>
                    <Text type="secondary">Эффект: {item.expectedEffect}</Text>
                  </Space>
                </Card>
              ))}
            </div>
          )}
          {coordinationQueues.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <Text strong>Что снимет перегруз</Text>
              {coordinationQueues.map((item) => (
                <Card key={`coord_queue_${item.queue}`} size="small" style={{ marginTop: 6 }}>
                  <Space direction="vertical" size={2}>
                    <Text strong>
                      {item.sequence}. {item.queue}: {item.targetCount}
                    </Text>
                    <Text type="secondary">{item.reason}</Text>
                    <Text>Эффект: {item.expectedRelief}</Text>
                    <Text>Ответственный отдел: {item.owner}</Text>
                  </Space>
                </Card>
              ))}
            </div>
          )}
          {coordinationCrossRole.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <Text strong>Что разблокирует процессы</Text>
              {coordinationCrossRole.map((item) => (
                <Card key={`coord_cross_${item.workflow}`} size="small" style={{ marginTop: 6 }}>
                  <Space direction="vertical" size={2}>
                    <Text strong>{item.workflow}</Text>
                    <Text>Блокировка: {item.blockedBy}</Text>
                    <Text>Перегруз отдела: {item.overloadedDepartment}</Text>
                    <Text type="secondary">Разблокирующее действие: {item.unblockAction}</Text>
                  </Space>
                </Card>
              ))}
            </div>
          )}
          <Space wrap style={{ marginTop: 12 }}>
            {roleActions.map((action) => (
              <Button key={action.key} size="small" onClick={() => navigate(action.to)}>
                {action.label}
              </Button>
            ))}
          </Space>
        </>
      )}
      {compact && blockerReasons.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <Text type="secondary">
            {blockerReasons[0].priorityLabel}: {blockerReasons[0].reason}. Ответственный: {blockerReasons[0].owner}. Действие: {blockerReasons[0].nextAction}.
          </Text>
          {coordinationOrdering[0] && (
            <div>
              <Text type="secondary">
                Сначала: {coordinationOrdering[0].nextAction}.
              </Text>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

